/**
 * NCC: the collection sidebar (loaded by blocks/filters.liquid).
 *
 * 1. Smart sticky. assets/ncc-theme.css pins the facets sidebar under the header, which strands its lower values
 *    whenever the sidebar is taller than the viewport (a 900px laptop with the Category tree open), and an inner
 *    scrollbar trapped the wheel (Miles, 2026-09-15). Now the sidebar pins under the header while it fits; when it
 *    is taller it travels with the page and pins at whichever edge the scroll direction reaches - the bottom edge
 *    scrolling down, the header edge scrolling up - so the page always scrolls and every value stays reachable.
 *    Three states: "top" (the stylesheet's sticky rule), "free" (relative, offset to where it was released) and
 *    "bottom" (sticky with a negative top so its bottom edge meets the viewport's).
 *
 * 2. Active-filter pills. The pills row Horizon renders above the first facet appears or disappears in a
 *    re-render, which shoved the facets down or up in one frame; the facets now slide there and the pills fade in.
 */
const SIDEBAR = '.facets-block-wrapper--vertical:not(#filters-drawer)';
const OFFSET_BELOW_HEADER = 24; // the sticky rule's "+ 24px" in assets/ncc-theme.css
const SLIDE = '320ms cubic-bezier(0.72, 0.14, 0.07, 0.96)';

/** @param {string} value */
const px = (value) => {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : 0;
};

class StickySidebar {
  /** @type {'top' | 'free' | 'bottom'} */
  state = 'top';

  /** @param {HTMLElement} sidebar */
  constructor(sidebar) {
    this.sidebar = sidebar;
    this.parent = /** @type {HTMLElement} */ (sidebar.parentElement);
    this.scroller = this.findScroller();
    this.lastScroll = this.scrollTop();
    // handled inside the scroll event, which fires before paint: a state switch is then painted with the scroll
    // itself and a fast wheel step never shows the sidebar overshooting its edge for a frame
    this.onScroll = () => this.update();
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', () => {
      this.scroller.removeEventListener('scroll', this.onScroll);
      this.scroller = this.findScroller();
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.lastScroll = this.scrollTop();
      this.set('top');
    });
    const observer = new ResizeObserver(() => this.onResize());
    observer.observe(sidebar);
    observer.observe(this.parent);
  }

  /** Horizon scrolls the desktop page inside .page-wrapper, phones and tablets scroll the window. */
  findScroller() {
    let node = this.sidebar.parentElement;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node;
      node = node.parentElement;
    }
    return window;
  }

  scrollTop() {
    return this.scroller === window ? window.scrollY : /** @type {HTMLElement} */ (this.scroller).scrollTop;
  }

  get topOffset() {
    return px(getComputedStyle(this.sidebar).getPropertyValue('--header-group-height')) + OFFSET_BELOW_HEADER;
  }

  /**
   * @param {'top' | 'free' | 'bottom'} state
   * @param {number} [offset] - the "free" state's distance below the sidebar's natural position
   */
  set(state, offset = 0) {
    this.state = state;
    const { style } = this.sidebar;
    if (state === 'top') {
      style.position = '';
      style.top = '';
    } else if (state === 'bottom') {
      style.position = 'sticky';
      style.top = `${window.innerHeight - this.sidebar.offsetHeight}px`;
    } else {
      style.position = 'relative';
      style.top = `${offset}px`;
    }
  }

  /**
   * Releases the sidebar where it is, so it travels with the page from there (clamped to its column). Its natural
   * position is measured, not derived from the grid (the collection grid has a toolbar row above the sidebar).
   */
  release() {
    const { sidebar, parent } = this;
    const before = sidebar.getBoundingClientRect().top;
    this.set('free', 0);
    const natural = sidebar.getBoundingClientRect().top;
    const parentStyle = getComputedStyle(parent);
    const columnBottom =
      parent.getBoundingClientRect().bottom - px(parentStyle.borderBottomWidth) - px(parentStyle.paddingBottom);
    const room = columnBottom - natural - sidebar.offsetHeight;
    this.set('free', Math.min(Math.max(before - natural, 0), Math.max(room, 0)));
  }

  update() {
    const { sidebar } = this;
    if (!sidebar.offsetHeight) return;

    const scroll = this.scrollTop();
    const delta = scroll - this.lastScroll;
    this.lastScroll = scroll;
    if (!delta) return;

    const viewport = window.innerHeight;
    const top = this.topOffset;
    const fits = sidebar.offsetHeight + top <= viewport;
    const rect = sidebar.getBoundingClientRect();

    if (delta > 0) {
      // scrolling down: a pinned sidebar that does not fit is released, a travelling one pins when its edge arrives
      if (this.state === 'top') {
        if (!fits && rect.top <= top + 1) this.release();
      } else if (this.state === 'free') {
        if (fits ? rect.top <= top : rect.bottom <= viewport) this.set(fits ? 'top' : 'bottom');
      }
    } else {
      // scrolling up: released from the bottom edge, pinned again once its top reaches the header
      if (this.state === 'bottom') {
        if (rect.bottom >= viewport - 1) this.release();
      } else if (this.state === 'free') {
        if (rect.top >= top) this.set('top');
      }
    }
  }

  /** The sidebar or its column changed height (the Category tree opening, the pills row, a shorter grid). */
  onResize() {
    if (!this.sidebar.offsetHeight) return;
    if (this.state === 'top') return;
    // pinned at the bottom, growing would shove the values up: keep the top where it is instead; travelling, re-clamp
    this.release();
  }
}

/**
 * Slides the facets to their new place when the active-filter pills above them appear or disappear.
 * @param {HTMLElement} form
 */
function slideFacetsUnderPills(form) {
  const pills = form.querySelector(':scope > .facets-remove');
  const facets = pills?.nextElementSibling;
  if (!(pills instanceof HTMLElement) || !(facets instanceof HTMLElement)) return;

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const offset = () => facets.getBoundingClientRect().top - form.getBoundingClientRect().top;
  let last = offset();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;

  const settle = () => {
    facets.style.transition = '';
    facets.style.transform = '';
    pills.style.transition = '';
    pills.style.opacity = '';
  };

  new MutationObserver(() => {
    const next = offset();
    const delta = last - next;
    last = next;
    if (!delta || reduceMotion.matches) return;

    clearTimeout(timer);
    facets.style.transition = 'none';
    facets.style.transform = `translateY(${delta}px)`;
    pills.style.transition = 'none';
    pills.style.opacity = delta < 0 ? '0' : '';
    facets.getBoundingClientRect(); // commit the start state before the transition is switched on
    facets.style.transition = `transform ${SLIDE}`;
    pills.style.transition = `opacity ${SLIDE}`;
    facets.style.transform = '';
    pills.style.opacity = '';
    timer = setTimeout(settle, 400);
  }).observe(pills, { childList: true, subtree: true });
}

const sidebar = document.querySelector(SIDEBAR);
if (sidebar instanceof HTMLElement && sidebar.parentElement) new StickySidebar(sidebar);
document.querySelectorAll('facets-form-component .facets__form').forEach((form) => {
  if (form instanceof HTMLElement) slideFacetsUnderPills(form);
});
